<template>
    <!-- Theme one -->
    <div v-if="themeStore.themeIndex === 1">
        <RechargeGiftOne :data-set="dataSet" @get-claim="getClaim" @get-history-list="getHistoryList"
            @go-to-deposit="goToDeposit" @get-next-page-reward-data="getNextPageRewardData"
            @reset-history-data="resetHistoryData" />
    </div>

    <!-- Theme two -->
    <div v-if="themeStore.themeIndex === 2">
        <RechargeGiftTwo :data-set="dataSet" @get-claim="getClaim" @get-history-list="getHistoryList"
            @go-to-deposit="goToDeposit" @get-next-page-reward-data="getNextPageRewardData"
            @reset-history-data="resetHistoryData" />
    </div>

    <!-- Theme three -->
    <div v-if="themeStore.themeIndex === 3">
        <RechargeGiftThree :data-set="dataSet" @get-claim="getClaim" @get-history-list="getHistoryList"
            @go-to-deposit="goToDeposit" @get-next-page-reward-data="getNextPageRewardData"
            @reset-history-data="resetHistoryData" />
    </div>

    <!-- Theme four -->
    <div v-if="themeStore.themeIndex === 4">
        <RechargeGiftFour :data-set="dataSet" @get-claim="getClaim" @get-history-list="getHistoryList"
            @go-to-deposit="goToDeposit" @get-next-page-reward-data="getNextPageRewardData"
            @reset-history-data="resetHistoryData" />
    </div>
    <CarteiraView ref="walletPopupRef" />
    <StatusPopup ref="statusRef" :is-show-success="isSuccess" :content="toastContent" />
    <LoadingScreen ref="loadingRef" />
</template>

<script setup lang="ts">
import { getActivityInformations } from '@/api/common';
import { ref, onMounted, computed } from 'vue';
import StatusPopup from '@/components/common/statusPopup.vue';
import LoadingScreen from '@/components/common/loadingScreen.vue';
import { getDefaultRewards, getTodayData, sumAmounts } from '@/utils/commonFunction';
import { getDailyRechargeRewardsClaim } from '@/api/activity';
import { useUserStore } from '@/stores/user';
import { DailyGift, FutureRewards, PAGE_SIZE, RechargeGiftData } from './supportScript';
import RechargeGiftOne from '@/themes/themeOne/rechargeGift/rechargeGift.vue';
import RechargeGiftTwo from '@/themes/themeTwo/rechargeGift/rechargeGift.vue';
import RechargeGiftThree from '@/themes/themeThree/rechargeGift/rechargeGift.vue';
import RechargeGiftFour from '@/themes/themeFour/rechargeGift/rechargeGift.vue';
import { useThemeStore } from '@/stores/theme';
import { useRouter } from 'vue-router';
import CarteiraView from '../carteiraView/carteiraView.vue';

const router = useRouter()
const themeStore = useThemeStore()
const userStore = useUserStore()
const isSuccess = ref(false)
const statusRef = ref();
const toastContent = ref('');
const loadingRef = ref();

const todayRechargeGiftDetails = ref<DailyGift>({
    day: 0,
    gift_rate: 0,
    turnover_times: 0,
    createTime: '',
    activeStatus: 0,
    dayOne: 0,
    dayTwo: 0,
    dayThree: 0,
    dayFour: 0,
    dayFive: 0,
    daySix: 0,
    daySeven: 0,
    status: 0,
    availableAmount: 0,
    givenAmount: 0,
    rewards: [],
    rechargeBonusStatus: 0
});
const historyList = ref<DailyGift[]>([])
const activityDescription = ref<string[]>([]);
const total = ref(0)
const pageNum = ref(1)
const pageSize = ref(PAGE_SIZE)
const weeklyRechargeGiftList = ref<DailyGift[]>([])
const cashbackTotal = ref(0)
const futureRewards = ref<FutureRewards[]>([])
const bannerIcon = ref('')
const walletPopupRef = ref()
const rechargeBonusStatus = ref(0)

const dataSet = computed<RechargeGiftData>(() => ({
    todayRechargeGiftDetails: todayRechargeGiftDetails.value,
    historyList: historyList.value,
    activityDescription: activityDescription.value,
    total: total.value,
    pageNum: pageNum.value,
    pageSize: pageSize.value,
    weeklyRechargeGiftList: weeklyRechargeGiftList.value,
    cashbackTotal: cashbackTotal.value,
    futureRewards: futureRewards.value,
    bannerIcon: bannerIcon.value,
    rechargeBonusStatus: rechargeBonusStatus.value
}))
const getActivityInfo = () => {
    loadingRef.value.open();
    let data = {
        activityId: "RECHARGE_GIFT",
        pageNum: pageNum.value,
        pageSize: 7
    }
    getActivityInformations(data).then(res => {
        loadingRef.value.close();
        bannerIcon.value = res.data?.floatIcon
        weeklyRechargeGiftList.value = res.data.config.rows
        todayRechargeGiftDetails.value = weeklyRechargeGiftList.value ? getTodayData(weeklyRechargeGiftList.value) : {}
        let days = JSON.parse(res.data?.activityConfig)?.recharge_gift_config.length
        futureRewards.value = todayRechargeGiftDetails.value ? todayRechargeGiftDetails.value?.rewards : getDefaultRewards(days)
        rechargeBonusStatus.value = JSON.parse(res.data?.activityConfig)?.rechargeBonusStatus

        if (!todayRechargeGiftDetails.value) {
            const today = new Date().toISOString().split('T')[0];
            let defaultData = {
                day: 0,
                gift_rate: 0,
                turnover_times: 0,
                createTime: today + ' 00:00:00',
                activeStatus: 0,
                dayOne: 0,
                dayTwo: 0,
                dayThree: 0,
                dayFour: 0,
                dayFive: 0,
                daySix: 0,
                daySeven: 0,
                status: 0,
                availableAmount: 0,
                givenAmount: 0,
                rewards: [],
                rechargeBonusStatus: 0
            }
            todayRechargeGiftDetails.value = defaultData
            futureRewards.value = getDefaultRewards(days)
        }
        cashbackTotal.value = sumAmounts(futureRewards.value)
        activityDescription.value = JSON.parse(res.data.activityIntro);
    }).catch(err => {
        if (err.code != 401) {
            loadingRef.value.close();
            showError(err);
        }
    })
}

const showError = (message: string) => {
    toastContent.value = message;
    isSuccess.value = false;
    statusRef.value.open()
}

const getNextPageRewardData = () => {
    if (total.value > pageSize.value * pageNum.value) {
        loadingRef.value.open()
        pageNum.value = pageNum.value + 1
        getHistoryList()
    }
}
const getHistoryList = () => {
    loadingRef.value.open();
    let data = {
        activityId: "RECHARGE_GIFT",
        pageNum: pageNum.value,
        pageSize: pageSize.value
    }
    getActivityInformations(data).then((res: any) => {
        historyList.value = [...historyList.value, ...res.data.config.rows]
        total.value = res.data.config.total
        loadingRef.value.close();
    }).catch(err => {
        if (err.code != 401) {
            loadingRef.value.close();
            showError(err);
        }
    })
}

const resetHistoryData = () => {
    total.value = 0
    pageNum.value = 1
    historyList.value = []
}

const showSuccess = (message: string) => {
    toastContent.value = message;
    isSuccess.value = true;
    statusRef.value.open()
}

const getClaim = () => {
    let data = {
        uid: userStore.id
    }
    loadingRef.value.open();
    getDailyRechargeRewardsClaim(data).then((res: any) => {
        loadingRef.value.close();
        showSuccess(res.msg)
        loadingRef.value.open();
        getActivityInfo()
    }).catch(err => {
        loadingRef.value.close();
        showError(err);
    })
}
const goToDeposit = (amount: any) => {
    userStore.setRechargeBonusType(4)
    if (themeStore.themeIndex === 1 || themeStore.themeIndex === 3 || themeStore.themeIndex === 4) {
        router.push({ path: '/sd-topup', query: { firstRechargeValue: amount } });
    }
    else if (themeStore.themeIndex === 2) {
        walletPopupRef?.value?.openPopup(0)
        userStore.setFirstRechargeAmount(amount)
    }
}
onMounted(() => {
    getActivityInfo()
})

</script>